import React, { memo, useRef, useState } from 'react';

import { useStore } from '@vromo/mobx-react';
import { Upload } from '@vromo/models';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import ProfileImage from '@vromo/ui/components/ProfileImage';
import theme from '@vromo/ui/components/theme';
import { css } from 'emotion';
import { lighten } from 'polished';
import { deserialize } from 'serializr';
import { RootStore } from '../../../../../stores/RootStore';
import Button from '@vromo/ui/components/Button';

const ProfilePictureEditor = () => {
    const fileInput = useRef<HTMLInputElement>();
    const file = useRef<File>();

    const {
        authStore: { graph, user }
    } = useStore<RootStore>('rootStore');

    const [url, setUrl] = useState(user.avatar?.url);
    const [loading, setLoading] = useState(false);
    const [changed, setChanged] = useState(false);

    const selectFile = (e) => {
        if (e.target.files.length) {
            file.current = e.target.files[0];
            setChanged(true);

            const reader = new FileReader();

            reader.onload = ({ target: { result } }: { target: { result: string } }) => {
                setUrl(result);
            };

            reader.readAsDataURL(file.current);
        }
    };

    const save = async () => {
        setLoading(true);

        try {
            const {
                items: [{ upload: s3Url, url }]
            } = await graph.root
                .graph()
                .user()
                .uploads()
                .post([{ type: file.current.type, link: 'avatar' }]);

            await fetch(s3Url, { method: 'PUT', body: file.current });

            user.avatar = deserialize(
                Upload,
                await graph.root.graph().user().avatar().post({ url })
            );
            setLoading(false);
            setChanged(false);
        } catch (e) {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
                className={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    border-radius: 8px;
                    padding: 20px;
                    cursor: pointer;
                    transition: all 300ms ease-in-out;

                    &:hover {
                        background-color: ${lighten(0.3, theme.primary)};
                    }
                `}
                onClick={() => !loading && fileInput?.current?.click()}>
                <ProfileImage url={url} user={user} size={150} />
                <a style={{ marginTop: 20 }}>Change Profile Photo</a>
                <input
                    ref={fileInput}
                    type="file"
                    onChange={selectFile}
                    style={{ display: 'none' }}
                    accept="image/png, image/jpeg"
                />
            </div>
            {loading && <FontAwesome icon="spinner" spin color={theme.primary} />}
            {!loading && changed && (
                <Button style={{ margin: 8 }} onClick={save}>
                    Save
                </Button>
            )}
        </div>
    );
};

export default memo(ProfilePictureEditor);
